// ./src/utils/couponPlaces.ts
//
// WHERE AN EVENT'S VOUCHERS ARE REDEEMED, AND THE WORDS PRINTED ON THEM (owner, 2026-10-25) — `event.settings.coupons`
// `redeemServicePointId` / `redeemAt[]` / `printHeader` / `printFooter`. Mirrors the API's `couponPlaceSettingsOf` /
// `couponRedeemPointId` (boundary/coupon.ts) — the server refuses a bad value on the save that changes it; this READS.
//
// ⚠ A place is a SOFT ref to a service point. It is resolved against the event's LIVE points
// (`ServicePointService.forEvent`), never loaded by id: a removed point, or one of a venue the event has left, is simply
// not printed.
// Pure (no React, no barrel imports) so the guards run it.

/** Mirrors `COUPON_PRINT_TEXT_MAX` — the input stops there; the server refuses past it. */
export const COUPON_PRINT_TEXT_MAX = 256;

export interface CouponRedeemAt {
  couponTypeId: string;
  servicePointId: string;
}

export interface CouponPlaceSettings {
  redeemServicePointId: string | null;
  redeemAt: CouponRedeemAt[];
  printHeader: string | null;
  printFooter: string | null;
}

/** The fields the server keys a refusal on — every one is read by the voucher settings drawer. */
export const COUPON_SETTINGS_FIELD = {
  REDEEM_POINT: "couponRedeemServicePointId",
  REDEEM_AT: "couponRedeemAt",
  PRINT_HEADER: "couponPrintHeader",
  PRINT_FOOTER: "couponPrintFooter",
} as const;

const UUID_TEXT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ⚠ The server's rule exactly: a uuid, lowercased — anything else is "not set" (and is not sent back as a change). */
const idText = (v: unknown): string | null => {
  const text = typeof v === "string" ? v.trim() : "";

  return UUID_TEXT_RE.test(text) ? text.toLowerCase() : null;
};

const printText = (v: unknown): string | null => {
  const text = typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";

  return text ? text.slice(0, COUPON_PRINT_TEXT_MAX) : null;
};

export const couponPlaceSettingsOf = (settings: { coupons?: any } | null | undefined): CouponPlaceSettings => {
  const raw = settings?.coupons ?? {};
  const seen = new Set<string>();
  const redeemAt: CouponRedeemAt[] = [];

  for (const one of Array.isArray(raw.redeemAt) ? raw.redeemAt : []) {
    const couponTypeId = idText(one?.couponTypeId);
    const servicePointId = idText(one?.servicePointId);

    // ⚠ The FIRST line for a type wins — the server's answer too.
    if (couponTypeId && servicePointId && !seen.has(couponTypeId)) {
      seen.add(couponTypeId);
      redeemAt.push({ couponTypeId, servicePointId });
    }
  }

  return {
    redeemServicePointId: idText(raw.redeemServicePointId),
    redeemAt,
    printHeader: printText(raw.printHeader),
    printFooter: printText(raw.printFooter),
  };
};

/** Where vouchers of `couponTypeId` are redeemed: the type's own place, else the event's default, else null. */
export const couponRedeemPointId = (places: CouponPlaceSettings, couponTypeId: string | null | undefined): string | null => {
  const key = idText(couponTypeId);
  const own = key ? places.redeemAt.find((r) => r.couponTypeId === key) : undefined;

  return own?.servicePointId ?? places.redeemServicePointId;
};
